import { Component, Input, OnInit } from '@angular/core';
import { INCOME_FORM_EMPLOYMENT_KEYS } from '../../../../constants/income-form-employment-constant';

interface EmploymentKey {
  key: string;
  title: string;
  type: string;
  order: number;
}

interface AmountRow {
  order: number;
  title: string;
  titleSecondary?: string;
  amount: number;
}

interface NonCashBenefitRow {
  particular: string;
  particularSecondary?: string;
  value: string | number;
  isAmount: boolean;
}

@Component({
    selector: 'app-income-from-employment-en-v2',
    templateUrl: './income-from-employment-en-v2.component.html',
    styleUrls: [
        './income-from-employment-en-v2.component.css',
        '../../../shared/return-view-text-fields.css',
    ],
    standalone: false
})
export class IncomeFromEmploymentEnV2Component implements OnInit {
  @Input() returnData: any;
  @Input() printActive: boolean = false;

  incomeFormEmploymentData: any[] = [];

  private readonly nonCashBenefitKeys = new Set([
    'IS_CHK_RENT_FREE_ACMDTN',
    'IS_VAL_OF_ACMDTN_RFA',
    'IS_CHK_ACMDTN_AT_CONCESS_RATE',
    'IS_VAL_OF_ACMDTN_ACR',
    'IS_RENT_PAID_BY_TAXPAYER_ACR',
    'IS_CHK_VEHICLE_FACILITY_PROVIDED',
    'IS_ENGINE_UPTO_2500_CC',
    'IS_VEHICLE_FACILITY_RECEIVED_IN_MONTHS',
    'IS_CHK_OTHER_NON_CASH_BENEFIT',
    'IS_PARTICULAR_ONCB',
    'IS_VAL_ONCB',
  ]);

  private readonly shareSchemeKeys = new Set([
    'IS_EMP_SHARE_SCM_SHARE_RECEIVED',
    'IS_EMP_SHARE_SCM_SHARE_RECEIVED_FMV',
    'IS_EMP_SHARE_SCM_SHARE_RECEIVED_ACQ_COST',
    'IS_EMP_SHARE_SCM_SHARE_SELL',
    'IS_EMP_SHARE_SCM_SHARE_SELL_VALUE',
    'IS_EMP_SHARE_SCM_SHARE_SELL_SHARE_ACQ_PRICE',
  ]);

  readonly amountParticulars = INCOME_FORM_EMPLOYMENT_KEYS
    .filter((k) => k.type === 'amount' && !this.nonCashBenefitKeys.has(k.key) && !this.shareSchemeKeys.has(k.key))
    .slice()
    .sort((a, b) => a.order - b.order) as EmploymentKey[];

  /** Text / dropdown / Yes-No fields shown above the amount table. */
  private readonly textParticulars = INCOME_FORM_EMPLOYMENT_KEYS
    .filter((k) => k.type === 'text' && k.key !== 'IS_SALARY_NO' && k.key !== 'IS_EMP_NAME' && !this.nonCashBenefitKeys.has(k.key) && !this.shareSchemeKeys.has(k.key))
    .slice()
    .sort((a, b) => a.order - b.order) as EmploymentKey[];

  private readonly yesNoKeys = new Set([
    'IS_CHK_SHAREHOLDER_DIRECTOR',
    'IS_CHK_IFEMP_EXEMPTED_BY_SRO',
  ]);

  private readonly vehicleTypeLabels: Record<string, string> = {
    UPTO_1500_CC: 'Upto 1500 CC',
    '1500_CC_TO_2000_CC': '1500 CC to 2000 CC',
    '2000_CC_TO_2500_CC': '2000 CC to 2500 CC',
    EXCEEDING_2500_CC: 'Exceeding 2500 CC',
    UPTO_2500_CC: 'Up to 2500 CC',
    UpTo2500CC: 'Up to 2500 CC',
    Above2500CC: 'Above 2500 CC',
    '1': 'Up to 2500 CC',
    true: 'Up to 2500 CC',
    false: 'Above 2500 CC',
  };

  constructor() { }

  getValue(item: any, key: string): any {
    return item ? item[key] : null;
  }

  hasValue(item: any, key: string): boolean {
    const val = this.getValue(item, key);
    return val !== null && val !== undefined && val !== '';
  }

  getEmployerName(item: any): string {
    return String(this.getValue(item, 'IS_EMP_NAME') || this.getValue(item, 'IS_OFC_NAME') || '');
  }

  getTextParticulars(item: any): EmploymentKey[] {
    return this.textParticulars.filter((field) => this.hasValue(item, field.key));
  }

  getTextFieldTitle(field: EmploymentKey): string {
    return field.title;
  }

  getTextFieldDisplayValue(item: any, field: EmploymentKey): string {
    if (field.key === 'IS_EMP_TYPE') {
      return this.getEmploymentTypeTitle(this.getValue(item, field.key));
    }
    if (this.yesNoKeys.has(field.key)) {
      return this.formatYesNo(this.getValue(item, field.key));
    }
    return String(this.getValue(item, field.key) ?? '');
  }

  getAmountRows(item: any): AmountRow[] {
    const rows: AmountRow[] = this.amountParticulars
      .filter((particular) => this.hasValue(item, particular.key))
      .map((particular) => ({
        order: particular.order,
        title: particular.title,
        amount: this.toNumber(this.getValue(item, particular.key)),
      }));

    const shareReceived = this.getShareReceivedRow(item);
    if (shareReceived) {
      rows.push(shareReceived);
    }

    const shareTransferred = this.getShareTransferredRow(item);
    if (shareTransferred) {
      rows.push(shareTransferred);
    }

    return rows.sort((a, b) => a.order - b.order);
  }

  getNonCashBenefitRows(item: any): NonCashBenefitRow[] {
    const rows: NonCashBenefitRow[] = [];

    if (this.isYes(this.getValue(item, 'IS_CHK_RENT_FREE_ACMDTN'))) {
      rows.push({
        particular: 'Rent Free Accommodation',
        value: this.toNumber(this.getValue(item, 'IS_VAL_OF_ACMDTN_RFA')),
        isAmount: true,
      });
    }

    if (this.isYes(this.getValue(item, 'IS_CHK_ACMDTN_AT_CONCESS_RATE'))) {
      const accommodationValue = this.toNumber(this.getValue(item, 'IS_VAL_OF_ACMDTN_ACR'));
      const rentPaid = this.toNumber(this.getValue(item, 'IS_RENT_PAID_BY_TAXPAYER_ACR'));
      rows.push({
        particular: 'Accommodation at Concessional Rate',
        particularSecondary: `Value of Accommodation (${this.formatAmount(accommodationValue)}) - Rent Paid by the Taxpayer (${this.formatAmount(rentPaid)})`,
        value: accommodationValue - rentPaid,
        isAmount: true,
      });
    }

    if (this.isYes(this.getValue(item, 'IS_CHK_VEHICLE_FACILITY_PROVIDED'))) {
      const vehicleTypeLabel = this.getVehicleTypeLabel(this.getValue(item, 'IS_ENGINE_UPTO_2500_CC'));
      const months = this.getValue(item, 'IS_VEHICLE_FACILITY_RECEIVED_IN_MONTHS');
      rows.push({
        particular: vehicleTypeLabel
          ? `Vehicle Facility Provided (${vehicleTypeLabel})`
          : 'Vehicle Facility Provided',
        value: `${months ?? ''} Months`.trim(),
        isAmount: false,
      });
    }

    if (this.isYes(this.getValue(item, 'IS_CHK_OTHER_NON_CASH_BENEFIT'))) {
      const otherParticular = this.getValue(item, 'IS_PARTICULAR_ONCB');
      rows.push({
        particular: otherParticular
          ? `Other Non-Cash Benefit (${otherParticular})`
          : 'Other Non-Cash Benefit',
        value: this.toNumber(this.getValue(item, 'IS_VAL_ONCB')),
        isAmount: true,
      });
    }

    return rows;
  }

  private getShareReceivedRow(item: any): AmountRow | null {
    if (
      !this.hasValue(item, 'IS_EMP_SHARE_SCM_SHARE_RECEIVED')
      && !this.hasValue(item, 'IS_EMP_SHARE_SCM_SHARE_RECEIVED_FMV')
      && !this.hasValue(item, 'IS_EMP_SHARE_SCM_SHARE_RECEIVED_ACQ_COST')
    ) {
      return null;
    }

    const fairValue = this.toNumber(this.getValue(item, 'IS_EMP_SHARE_SCM_SHARE_RECEIVED_FMV'));
    const acquisitionCost = this.toNumber(this.getValue(item, 'IS_EMP_SHARE_SCM_SHARE_RECEIVED_ACQ_COST'));

    return {
      order: 67,
      title: 'Employee Share Schemes (Share Received)',
      titleSecondary: `Fair Value of Share (${this.formatAmount(fairValue)}) - Cost of Acquisition (${this.formatAmount(acquisitionCost)})`,
      amount: fairValue - acquisitionCost,
    };
  }

  private getShareTransferredRow(item: any): AmountRow | null {
    if (
      !this.hasValue(item, 'IS_EMP_SHARE_SCM_SHARE_SELL')
      && !this.hasValue(item, 'IS_EMP_SHARE_SCM_SHARE_SELL_VALUE')
      && !this.hasValue(item, 'IS_EMP_SHARE_SCM_SHARE_SELL_SHARE_ACQ_PRICE')
    ) {
      return null;
    }

    const transferValue = this.toNumber(this.getValue(item, 'IS_EMP_SHARE_SCM_SHARE_SELL_VALUE'));
    const acquisitionCost = this.toNumber(this.getValue(item, 'IS_EMP_SHARE_SCM_SHARE_SELL_SHARE_ACQ_PRICE'));

    return {
      order: 70,
      title: 'Employee Share Schemes (Transfer of Share Right)',
      titleSecondary: `Transfer Value (${this.formatAmount(transferValue)}) - Cost of Acquisition (${this.formatAmount(acquisitionCost)})`,
      amount: transferValue - acquisitionCost,
    };
  }

  getEmploymentTypeTitle(employmentType: any): string {
    const v = employmentType === null || employmentType === undefined ? '' : String(employmentType).trim();
    switch (v) {
      case '1':
      case 'GI':
        return 'Government Pay Scale (Payment through iBAS++)';
      case '2':
      case 'G':
        return 'Government Pay Scale (Payment not through iBAS++)';
      case '3':
      case 'NG':
        return 'Private/Other than Government Pay Scale';
      case '4':
      case 'SRO':
        return 'Salary Subject to Reduced Tax Rate';
      default:
        return v;
    }
  }

  private getVehicleTypeLabel(vehicleType: any): string {
    if (vehicleType === null || vehicleType === undefined || vehicleType === '' || vehicleType === '0' || vehicleType === 0) {
      return '';
    }
    const key = String(vehicleType);
    return this.vehicleTypeLabels[key] || this.vehicleTypeLabels[vehicleType as any] || key.replace(/_/g, ' ');
  }

  private isYes(value: any): boolean {
    return this.formatYesNo(value) === 'Yes';
  }

  private toNumber(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    if (typeof value === 'number' && !isNaN(value)) {
      return value;
    }
    const parsed = Number(String(value).replace(/,/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }

  private formatAmount(value: number): string {
    const amount = Number(value) || 0;
    const absolute = Math.abs(amount);
    const formatted = absolute.toLocaleString('en-IN');
    return amount < 0 ? `-${formatted}` : formatted;
  }

  private formatYesNo(value: any): string {
    if (
      value === true
      || value === 1
      || value === '1'
      || value === 'true'
      || value === 'Yes'
      || value === 'YES'
      || value === 'T'
    ) {
      return 'Yes';
    }
    if (
      value === false
      || value === 0
      || value === '0'
      || value === 'false'
      || value === 'No'
      || value === 'NO'
      || value === 'F'
    ) {
      return 'No';
    }
    return String(value ?? '');
  }

  ngOnInit(): void {
    const salaryData = this.returnData?.incomeData?.salaryData;
    this.incomeFormEmploymentData = Array.isArray(salaryData) ? salaryData : [];
  }
}
